import { useEffect, useState } from "react";
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
} from "firebase/auth";
import { firebaseAuth, isFirebaseConfigured } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function FirebaseAuthDemo() {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(!isFirebaseConfigured);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!firebaseAuth) return;
    const unsub = onAuthStateChanged(firebaseAuth, (u) => {
      setUser(u);
      setReady(true);
    });
    return unsub;
  }, []);

  const run = async (fn: () => Promise<unknown>, okMessage: string) => {
    if (!firebaseAuth) return;
    setBusy(true);
    try {
      await fn();
      toast.success(okMessage);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Firebase Auth</CardTitle>
          <CardDescription>
            A standalone check of Firebase sign in and sign out. Your main account stays as it is.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!isFirebaseConfigured ? (
            <p className="text-sm text-muted-foreground">
              Firebase is not configured yet. Add your <code>VITE_FIREBASE_*</code> values, then reload
              this page.
            </p>
          ) : !ready ? (
            <p className="text-sm text-muted-foreground">Checking your session…</p>
          ) : user ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge>Signed in</Badge>
                {user.emailVerified && <Badge variant="secondary">Email verified</Badge>}
              </div>
              <dl className="text-sm space-y-1">
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Name</dt>
                  <dd className="truncate">{user.displayName ?? "—"}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Email</dt>
                  <dd className="truncate">{user.email ?? "—"}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">UID</dt>
                  <dd className="truncate font-mono text-xs">{user.uid}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Provider</dt>
                  <dd className="truncate">{user.providerData[0]?.providerId ?? "—"}</dd>
                </div>
              </dl>
              <Button
                className="w-full min-h-12"
                variant="secondary"
                disabled={busy}
                onClick={() => run(() => signOut(firebaseAuth!), "Signed out")}
              >
                Sign out
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <Badge variant="secondary">Signed out</Badge>
              <div className="space-y-2">
                <Label htmlFor="fb-email">Email</Label>
                <Input
                  id="fb-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fb-password">Password</Label>
                <Input
                  id="fb-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  className="flex-1 min-h-12"
                  disabled={busy || !email || !password}
                  onClick={() =>
                    run(
                      () => signInWithEmailAndPassword(firebaseAuth!, email, password),
                      "Welcome back",
                    )
                  }
                >
                  Sign in
                </Button>
                <Button
                  className="flex-1 min-h-12"
                  variant="outline"
                  disabled={busy || !email || !password}
                  onClick={() =>
                    run(
                      () => createUserWithEmailAndPassword(firebaseAuth!, email, password),
                      "Account created",
                    )
                  }
                >
                  Create account
                </Button>
              </div>
              <Button
                className="w-full min-h-12"
                variant="secondary"
                disabled={busy}
                onClick={() =>
                  run(
                    () => signInWithPopup(firebaseAuth!, new GoogleAuthProvider()),
                    "Signed in with Google",
                  )
                }
              >
                Continue with Google
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
